/* eslint-disable react-hooks/exhaustive-deps */
import { useBoundValue } from "lib/@components/binding/use-bound-value"
import { useEffect, useMemo } from "react"
import { importerAssetFields } from "routes/asset-register/plugins/regime-display/asset-fields.mjs"
import { ensureArray } from "lib/ensure-array"
import { safeRegExp } from "library/safe-regexp.mjs"
import {
    Box,
    Card,
    Stack,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useTheme,
} from "@mui/material"
import { Bound } from "lib/@components/binding/Bound"
import { BoundCheckBox } from "lib/@components/standard-checkbox"
import { SaveCurrentMapping } from "routes/asset-register/plugins/excel-import/save-current-mapping"
import { convertCellToTextOrDate } from "./convert-cell-to-text-or-date"
import { Repeat } from "lib/@components/repeat"
import { FieldHeader } from "./field-header"
import { ImportRow } from "./import-row"
import { FieldSelector } from "routes/asset-register/plugins/excel-import/field-selector"
import { useDebouncedId } from "lib/use-debounced-id"
import { PreviewMappingInfo } from "routes/asset-register/plugins/excel-import/preview-mapping-info"
import { PreviewMappingAlert } from "slot-definitions"
import { PreviewAlert } from "event-definitions"
import { useBoundContext } from "lib/@components/binding/use-bound-context"

const rowFilterField = {
    field: "rowFilter",
    label: "Row Filter",
    description: "Whether the row should be imported, leave blank for no filter",
    importance: 0.1,
    allowAdditional: true,
    noSummary: true,
    allowFixedValue: false,
}

export function PreviewMapping({ canContinue }) {
    const [sheet] = useBoundValue("importFromSheet")
    const [row] = useBoundValue("headerRow")

    const [mappings] = useBoundValue("fields", {})
    const [isOk] = useBoundValue("previewOk")
    const theme = useTheme()
    const debounced = useDebouncedId(600)
    const { register } = useBoundContext()
    const usesUnrefinedTypes = !!register?.options?.originalTypes
    const rowFilterConfig = useMemo(() => buildRowFilterConfig(mappings), [mappings])
    const rowFilterGetter = useMemo(() => getFieldGetter(mappings, "rowFilter"), [mappings])
    const typeGetter = useMemo(() => getFieldGetter(mappings, "type"), [mappings])
    const tagGetter = useMemo(() => getFieldGetter(mappings, "tag"), [mappings])

    useEffect(debounced, [mappings])

    const fields = useMemo(
        () =>
            importerAssetFields
                .filter((f) => !f.notInAssetImport)
                .map((c) => {
                    const mapping = mappings[c.field]
                    if (mapping) {
                        return {
                            field: c.field,
                            label: c.label,
                            get: (row) =>
                                mapping === "*UNIQUE"
                                    ? "# Generated Unique Code #"
                                    : ensureArray(mapping)
                                          .map((m) => convertCellToTextOrDate(row, m.column, mappings))
                                          .join(" "),
                        }
                    }
                    const fixedMapping = mappings[c.field + "_fixed"]
                    if (fixedMapping) {
                        return { field: c.field, label: c.label, get: () => fixedMapping }
                    }
                    return null
                })
                .filter(Boolean),
        [debounced.id]
    )

    const rows = useMemo(() => {
        if (!sheet) return []

        const allRows = sheet.getRows(row + 1, sheet.rowCount - row)
        if (!rowFilterConfig.hasRowFilter) return allRows.slice(0, 100)

        const result = []
        for (const r of allRows) {
            const include = shouldIncludeRow(rowFilterGetter ? rowFilterGetter(r) : "", rowFilterConfig)
            if (!include) continue
            result.push(r)
            if (result.length > 100) break
        }
        return result
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sheet, row, rowFilterConfig, rowFilterGetter, debounced.id])

    const errorLookup = useMemo(() => {
        if (!rows || !fields || !mappings) return {}

        const counts = {}

        rows.forEach((row) => {
            fields.forEach((field) => {
                try {
                    const mappingLookup = mappings[field.field]
                    const fixedMapping = mappings[field.field + "_fixed"]
                    if (fixedMapping || mappingLookup === "*UNIQUE") return

                    const key = mappings[field.field]["name"]
                    if (!key) return
                    const mapping = mappings[field.field]
                    const mappingArray = Array.isArray(mapping) ? mapping : [mapping]

                    mappingArray.forEach(({ column }) => {
                        try {
                            convertCellToTextOrDate(row, column, mappings, true)
                        } catch (e) {
                            counts[key] = counts[key] || 0
                            counts[key]++
                        }
                    })
                } catch (e) {
                    console.log(e)
                }
            })
        })

        return counts
    }, [rows, fields, mappings])

    const missingTypeInfo = useMemo(() => {
        if (!usesUnrefinedTypes || !sheet) return { count: 0, samples: [], requiresMapping: false }
        if (!typeGetter) return { count: 0, samples: [], requiresMapping: true }

        const allRows = sheet.getRows(row + 1, sheet.rowCount - row)
        let missingCount = 0
        const samples = []

        for (const r of allRows) {
            if (rowFilterConfig.hasRowFilter) {
                const include = shouldIncludeRow(rowFilterGetter ? rowFilterGetter(r) : "", rowFilterConfig)
                if (!include) continue
            }
            const typeValue = normalizeValue(typeGetter(r))
            if (typeValue) continue
            missingCount++
            if (samples.length < 5) {
                const tagValue = normalizeValue(tagGetter ? tagGetter(r) : "")
                samples.push(tagValue || `Row ${r.number}`)
            }
        }

        return { count: missingCount, samples, requiresMapping: false }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [usesUnrefinedTypes, sheet, row, typeGetter, tagGetter, rowFilterConfig, rowFilterGetter, debounced.id])

    useEffect(() => {
        const blockedByMissingTypes =
            usesUnrefinedTypes && (missingTypeInfo.requiresMapping || missingTypeInfo.count > 0)
        canContinue(isOk && !blockedByMissingTypes)
    }, [canContinue, isOk, missingTypeInfo, usesUnrefinedTypes])

    useEffect(() => {
        PreviewAlert.raise({
            info: { invalidDates: !!Object.keys(errorLookup ?? {}).length },
        })
    }, [errorLookup])

    useEffect(() => {
        if (!usesUnrefinedTypes) {
            PreviewAlert.raise({ error: { missingAssetTypes: false } })
            return
        }
        if (missingTypeInfo.requiresMapping) {
            PreviewAlert.raise({
                error: {
                    missingAssetTypes:
                        'Using "Use Unrefined Types" requires mapping the "Asset Type" column before importing.',
                },
            })
            return
        }
        if (missingTypeInfo.count > 0) {
            PreviewAlert.raise({
                error: { missingAssetTypes: missingTypeInfo },
            })
            return
        }
        PreviewAlert.raise({ error: { missingAssetTypes: false } })
    }, [missingTypeInfo, usesUnrefinedTypes])

    PreviewMappingAlert.usePlug(<MissingAssetTypeAlert />)
    PreviewMappingAlert.usePlug(<InvalidDatesAlert invalidDates={errorLookup} />)

    return (
        <Stack spacing={1} sx={{ mt: 3, mb: 1 }}>
            <Box>
                <Typography variant="body2">Filter</Typography>
                <Typography variant="caption" color="text.secondary">
                    Only records matching the filter will be imported.
                </Typography>
            </Box>
            <Bound onChange={debounced}>
                <FieldSelector item={rowFilterField}></FieldSelector>
            </Bound>
            <Typography variant="body2">Preview</Typography>
            <Bound fields={fields}>
                <Card>
                    <TableContainer
                        sx={{ "& tr": { whiteSpace: "nowrap" }, width: 1, overflow: "auto", maxHeight: 216 }}
                    >
                        <Table
                            stickyHeader
                            size="small"
                            data-cy="excel-import-preview-mapping-table"
                            sx={{
                                "& .MuiTableCell-root": {
                                    borderBottom: "1px solid" + theme.palette.grey[500_24],
                                    height: "53px",
                                },
                                "& .MuiTableCell-head": {
                                    borderBottomLeftRadius: "0 !important",
                                    borderBottomRightRadius: "0 !important",
                                },
                            }}
                        >
                            <TableHead>
                                <TableRow>
                                    <Repeat list={fields} item={<FieldHeader />} />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <Repeat list={rows} item={<ImportRow />} />
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>
            </Bound>
            <PreviewMappingInfo />
            <SaveCurrentMapping />
            <BoundCheckBox
                data-cy="accept-mapping-preview-checkbox"
                labelRight
                field="previewOk"
                label="The preview of the data is correct, I'm ready to import it."
            />
        </Stack>
    )
}

function MissingAssetTypeAlert() {
    const { error = {} } = useBoundContext()
    const details = error?.missingAssetTypes

    if (!details || details === false) return null

    if (typeof details === "string") {
        return (
            <Typography data-cy="missing-asset-types-alert" variant="body2">
                {details}
            </Typography>
        )
    }

    const { count = 0, samples = [] } = details
    if (!count) return null

    const sampleText = samples.length ? ` (e.g. ${samples.join(", ")})` : ""

    return (
        <Typography data-cy="missing-asset-types-alert" variant="body2">
            Asset Type is required when using "Use Unrefined Types". {count} asset
            {count === 1 ? "" : "s"} in this file have no type{sampleText}. Fill the "Asset Type" column or switch to
            AI-derived types.
        </Typography>
    )
}

function InvalidDatesAlert({ invalidDates }) {
    const { info = {} } = useBoundContext()
    if (!info.invalidDates || !invalidDates || !Object.keys(invalidDates ?? {}).length) return null

    const fields = Object.keys(invalidDates)

    const fieldList =
        fields.length === 1
            ? fields[0]
            : fields.length === 2
              ? fields.join(" and ")
              : `${fields.slice(0, -1).join(", ")} and ${fields.slice(-1)}`

    return (
        <>
            <Typography data-cy="invalid-dates-alert" variant="body2">
                Invalid dates found in: {fieldList}:
            </Typography>
            {fields.map((field) => (
                <Typography data-cy="invalid-dates-detail" key={field} variant="body2" color="textSecondary">
                    - {field}: {invalidDates[field]} invalid
                </Typography>
            ))}
        </>
    )
}

function buildRowFilterConfig(mappings) {
    const hasRowFilter = Boolean(mappings?.rowFilter || mappings?.rowFilter_fixed)
    if (!hasRowFilter) return { hasRowFilter: false }

    const filterPattern = (mappings?.rowFilter_extra ?? "").trim()
    const filterList =
        hasRowFilter &&
        new Set(
            mappings?.rowFilter_list
                ?.split(",")
                .map((s) => s.trim())
                .filter(Boolean)
        )
    const filterMode = hasRowFilter && (mappings?.rowFilter_type ?? "notblank")
    let rowFilterRegex = null
    if (filterPattern) {
        rowFilterRegex = safeRegExp(filterPattern)
    }
    return { hasRowFilter, filterPattern, filterList, filterMode, rowFilterRegex }
}

function shouldIncludeRow(value, { hasRowFilter, filterMode, filterList, filterPattern, rowFilterRegex }) {
    if (!hasRowFilter) return true
    const comparison = `${value ?? ""}`
    switch (filterMode) {
        case "notblank":
            return !!comparison
        case "list":
            return !filterList || filterList.has(comparison)
        case "regex":
            if (filterPattern) {
                return rowFilterRegex ? rowFilterRegex.test(comparison) : true
            }
            return true
        default:
            return true
    }
}

function getFieldGetter(mappings = {}, field) {
    const mapping = mappings[field]
    if (mapping === "*UNIQUE") return () => ""
    if (mapping) {
        return (row) =>
            ensureArray(mapping)
                .map((m) => convertCellToTextOrDate(row, m.column, mappings))
                .join(", ")
    }
    const fixedMapping = mappings[`${field}_fixed`]
    if (fixedMapping) return () => fixedMapping
    return null
}

function normalizeValue(value) {
    if (value == null) return ""
    const normalized = `${value}`.trim()
    return normalized === "null" ? "" : normalized
}
